import { FC, useRef, useState } from "react";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { useCostTypeCategoriesApi } from "../../../http/costTypeCategories/api";
import { CostTypeCategory } from "../../../http/costTypeCategories/types";
import { PaginationQueryWithFields } from "../../../types";
import { isWithinLast48h } from "../../../types/utils";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../../ui/ListItemMenu";
import ModalDialog from "../../ui/ModalDialog";
import NewItemIcon from "../../ui/NewItemIcon";
import Table from "../../ui/Table";
import CostTypeCategoryHistoryDialog from "./CostTypeCategoryHistoryDialog";
import CreateCostTypeCategoryButton from "./CreateCostTypeCategoryButton";
import DeleteCostTypeCategoryDialog from "./DeleteCostTypeCategoryDialog";
import EditCostTypeCategoryDialog from "./EditCategoryButton";

const COST_TYPE_CATEGORY_FIELDS = ["id", "createdAt", "name", "versionNumber"] as const;
type CostTypeCategoryField = (typeof COST_TYPE_CATEGORY_FIELDS)[number];
type CostTypeCategorySearch = Pick<CostTypeCategory, CostTypeCategoryField>;

interface CostTypeCategoriesProps {
  onClose: () => void;
}

const CostTypeCategories: FC<CostTypeCategoriesProps> = ({ onClose }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const costTypeCategoriesApi = useCostTypeCategoriesApi();
  const filters = useListFilters({ isSearchable: true, prefix: "ctc" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const [itemToEdit, setItemToEdit] = useState<CostTypeCategorySearch>();
  const [itemToDelete, setItemToDelete] = useState<CostTypeCategorySearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<CostTypeCategorySearch>();

  const query: PaginationQueryWithFields<CostTypeCategorySearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: COST_TYPE_CATEGORY_FIELDS,
  };

  const result = costTypeCategoriesApi.useAdminCostTypeCategories(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: CostTypeCategorySearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.costTypeCategories.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.costTypeCategories.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.costTypeCategories.plural}
        preTitleButton={userSettings.permissions.costTypeCategories.insert && <CreateCostTypeCategoryButton />}
      >
        <Table<CostTypeCategorySearch>
          filters={filters}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="category.name" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.name}
              </td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </ModalDialog>

      {!!itemToDelete && (
        <DeleteCostTypeCategoryDialog costTypeCategoryId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToEdit && (
        <EditCostTypeCategoryDialog costTypeCategoryId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />
      )}

      {!!itemToShowVersions && (
        <CostTypeCategoryHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default CostTypeCategories;
